import React, { FC, useEffect, useRef, useState, useMemo } from "react";
import {
	Grid,
	GridCellProps,
	GridCellsSettings,
	GridColumn,
	GridColumnProps,
	GridCustomCellProps,
	GridCustomHeaderCellProps,
	GridDataStateChangeEvent,
	GridDetailRowProps,
	GridExpandChangeEvent,
	GridFilterChangeEvent,
	GridFilterOperators,
	GridGroupableSettings,
	GridHeaderCellProps,
	GridRowProps,
	GridSortSettings,
} from "@progress/kendo-react-grid";
import styles from "styles.module.css";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import {
	ExcelExport,
	ExcelExportProps,
} from "@progress/kendo-react-excel-export";

import { OverLayLoading } from "ui/Loading";
import {
	Pager,
	PagerProps,
	setExpandedState,
	setGroupIds,
} from "@progress/kendo-react-data-tools";
import {
	AggregateDescriptor,
	CompositeFilterDescriptor,
	DataResult,
	GroupDescriptor,
	process,
	State,
} from "@progress/kendo-data-query";
import { loadMessages } from "@progress/kendo-react-intl";

import { t } from "i18next";

import { handleFilteredItem, handleGroupEmptyMessage } from "utils/statUtils";
import { CustomRowRender, FilterHeaderCellRender } from "./CustomGrid";
import { CustomError } from "interface";
import { setError } from "store/error";
import i18n from "i18n";
import { messagesEn, messagesNo } from "./utilities/localization";
import { COLOR } from "utils/constant";
const unUsedHeight = 370; // 370px is the height of the filter and the header
loadMessages(messagesEn, "en");
loadMessages(messagesNo, "no");
interface GridPageProps {
	filter?: any;
	dataKey?: string;
	isExpandable?: boolean;
	forceUpdate?: boolean;
	cells?: GridCellsSettings;
	// handleFilteredItem?: (filter: any) => any[];
	aggregateFields?: AggregateDescriptor[];
	groupHeaderCell?: React.ComponentType<GridCustomCellProps>;
	group?: GroupDescriptor[];
	groupLargeColumn?: { field: string; width: number }[];
	defaultAggregates?: AggregateDescriptor[];
	showSelectedFilters?: boolean;
	headerCellRender?: React.ComponentType<GridCustomHeaderCellProps>;
	pageSizes?: number[];
	pageSize?: number;
	groupable?: GridGroupableSettings | boolean;
	pageable?: boolean;
	//  footerItemsField?: AggregateDescriptor[],
	className?: string;
	apiMethod?: (filter: any) => Promise<any>;
	serverFilter?: boolean;
	filterOperators?: GridFilterOperators;
	filterable?: boolean;
	serverFilterMap?: {
		/// The field name that the server expects
		serverFieldName: string;
		/// The filter field on the grid
		filterFieldName: string;
	}[];
	// onDataStateChange?: (state: State) => void;
	handleStateChange?: (state: State) => void;
	defaultFilter?: CompositeFilterDescriptor;
	exportGridAsPDFRef?: React.RefObject<GridPDFExport>;
	exportGridAsExcelRef?: React.RefObject<ExcelExport>;
	excelProps?: Partial<ExcelExportProps>;
	gridColumns: GridColumnProps[];
	gridStyle?: React.CSSProperties;
	itemAboveHeight?: number;
	gridData?: any[];
	loading?: boolean;
	detailPage?: React.ComponentType<GridDetailRowProps>;
	gridHeight?: number;
	gridMaxHeight?: number;
	sortable?: GridSortSettings;
	handelShowRowBgColor?: (rowData: GridRowProps) => string;
	handelShowRowTextColor?: (rowData: GridRowProps) => string;
	highlightFilterColumns?: boolean;
	useDefaultRowTextColor?: boolean;
	hideVirtualScroll?: boolean;
	lastColBtns?: GridColumnProps;
}

const GridWithGroupPaging: FC<GridPageProps> = ({
	dataKey = "",
	filter,
	exportGridAsExcelRef,
	exportGridAsPDFRef,
	isExpandable = true,
	gridHeight = 900,
	itemAboveHeight = 0,
	handelShowRowBgColor,
	aggregateFields = [],
	forceUpdate,
	gridData,
	groupLargeColumn = [],
	showSelectedFilters = false,
	pageable = true,
	pageSizes = [15, 30, 50, 100, 250, 500, 750, 1000],
	pageSize = 15,
	groupable = { footer: "visible", enabled: false },
	serverFilter = false,
	serverFilterMap = [],
	sortable,
	filterable = false,
	hideVirtualScroll = false,
	handelShowRowTextColor = () => COLOR.Grey20,
	cells,
	...props
}) => {
	const [result, setResult] = useState<DataResult>({
		data: gridData || [], // Provide default here instead
		total: gridData?.length || 0,
	});
	const [loading, setLoading] = useState<boolean>(false);
	const dataRef = useRef<any[]>(gridData || []);

	const filterKey = useMemo(() => JSON.stringify(filter ?? {}), [filter]);

	const [dataState, setDataState] = React.useState<State & any>({
		...(pageable
			? {
					skip: 0,
					take: pageSize,
					pageSize: pageSize,
				}
			: {}),

		group: props.group,
	});

	const [pageHeight, setPageHeight] = useState(window.innerHeight);
	const [collapsedState, setCollapsedState] = React.useState<string[]>([]);
	const [selectedFilters, setSelectedFilters] = React.useState("");

	const overlayRef = useRef<HTMLDivElement>(null);
	const filterRef = useRef<HTMLDivElement>(null);
	const filterHeightRef = useRef(0);
	const internalFilter = useRef<any>(null);
	const gridRef = useRef<HTMLDivElement>(null);
	useEffect(() => {
		const updateHeight = () => setPageHeight(window.innerHeight);
		window.addEventListener("resize", updateHeight);

		return () => window.removeEventListener("resize", updateHeight);
	}, []);

	useEffect(() => {
		// if (props.apiMethod && gridData && gridData.length > 0) {
		// 	console.warn(
		// 		"Grid data is already loaded and apiMethod is not needed set at the same time"
		// 	);
		// 	console.warn(
		// 		"Please remove the apiMethod from the props or set the gridData to empty array"
		// 	);
		// }

		if (!props.apiMethod) return;
		handleApiRequest();
	}, [dataState, filterKey]);

	useEffect(() => {
		if (!gridData) return;
		const __dataSate = {
			skip: 0,
			take: pageable ? dataState.take : gridData.length,
			pageSize: pageable ? dataState.pageSize : gridData.length,
		};
		setDataState(__dataSate);
		const newDataState = process(gridData, __dataSate);
		setGroupIds({ data: newDataState.data, group: dataState.group });
		setResult(newDataState);
		dataRef.current = gridData;
	}, [gridData, forceUpdate]);

	useEffect(() => {
		if (dataState.filter) {
			if (handleFilteredItem) {
				const filters = handleFilteredItem(dataState.filter);
				const filterItems = filters.map((filter) => {
					const col = props.gridColumns.find(
						(col) => col.field === filter.field,
					);
					let field = filter.field;
					if (col && col.title) {
						field = col.title;
					}
					return ` ${field} : [${filter.values.map((v: any) => `"${v}"`)}]  `;
				});

				setSelectedFilters(filterItems.toString());
			}
			if (filterRef.current) {
				filterHeightRef.current = filterRef.current.clientHeight;
			}
		}

		// if (props.apiMethod && serverFilter) {
		// 	props
		// 		.apiMethod(filter)
		// 		.then((data) => {
		// 			const newDataState = process(data, dataState);
		// 			setGroupIds({ data: newDataState.data, group: dataState.group });
		// 			setResult(newDataState);
		// 		})
		// 		.catch((error) => {
		// 			console.error("Error fetching data:", error);
		// 			if (error.response?.data) {
		// 				setError(new CustomError("Something went wrong", "error"));
		// 			} else {
		// 				setError(new CustomError(error.message));
		// 			}
		// 		});
		// }
	}, [dataState.filter]);

	useEffect(() => {
		handleGroupEmptyMessage(dataState);
	}, [dataState.filter]);

	const handleApiRequest = () => {
		if (!props.apiMethod) {
			console.error("No API method provided for data fetching.");
			return;
		}

		let newFilter = {
			...(filter ?? {}),
		};

		if (serverFilter && internalFilter.current) {
			newFilter = {
				...newFilter,
				...dataState,
				...internalFilter.current,
			};
		}
		setLoading(true);

		props
			.apiMethod(newFilter)
			.then((data) => {
				dataRef.current = data || [];

				const newDataState = process(data || [], dataState);
				setGroupIds({ data: newDataState.data, group: dataState.group });
				setResult(newDataState);
			})
			.catch((error) => {
				console.error("Error fetching data:", error);
				if (error.response?.data) {
					setError(new CustomError("Something went wrong", "error"));
				} else {
					setError(new CustomError(error.message));
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const handleDataStateChange = (dataState: State) => {
		// Handle undefined gridData
		if (!gridData) {
			return {
				data: [],
				total: 0,
			};
		}

		const newDataState = process(gridData, dataState);
		setGroupIds({ data: newDataState.data, group: dataState.group });

		return newDataState;
	};

	const onDataStateChange = (e: GridDataStateChangeEvent) => {
		e.dataState.group = e.dataState.group?.map((group) => ({
			...group,
			aggregates: group.aggregates
				? [...aggregateFields, ...group.aggregates]
				: aggregateFields,
		}));
		let data = handleDataStateChange(e.dataState);
		setResult(data);
		// setCurrentData();
		handleFilterMap(e.dataState);
		setDataState(e.dataState);
	};

	const newData = setExpandedState({
		data: result.data,
		collapsedIds: collapsedState,
	});

	// const handleFilterChange = (e: GridFilterChangeEvent) => {
	// 	handleFilterMap({ ...dataState, filter: e.filter });
	// };

	const handleFilterMap = (state: State) => {
		if (props.handleStateChange) {
			props.handleStateChange(state);
		}
		if (!state.filter) {
			return;
		}

		setDataState(state);
		if (handleFilteredItem) {
			const filters = handleFilteredItem(state.filter);

			let mapObject: any = {};

			filters.forEach((filter) => {
				const map = serverFilterMap.find(
					(map) => map.filterFieldName === filter.field,
				);
				if (map) {
					mapObject[map.serverFieldName] = filter.values[0];
				}
			});

			internalFilter.current = mapObject;
			if (props.apiMethod) {
				handleApiRequest();
			}
		}
	};

	const currentHeight =
		pageHeight - unUsedHeight - itemAboveHeight - filterHeightRef.current * 2;

	return (
		<div style={{}}>
			<div ref={overlayRef} style={{}}>
				<OverLayLoading show={loading || props?.loading === true} />
			</div>

			{showSelectedFilters && (
				<div ref={filterRef}>Filter: {selectedFilters}</div>
			)}

			<div
				style={{
					marginTop: 30,
					// maxHeight: currentHeight,
					overflowX: "auto",
				}}
				ref={gridRef}
			>
				<ExcelExport
					data={process(dataRef.current, { group: dataState.group }).data}
					group={dataState.group}
					collapsible={true}
					{...(props?.excelProps || {
						group: dataState.group,
					})}
					ref={exportGridAsExcelRef}
				>
					{/* {ExcelExportColumnProps.map((colProps, index) => (
						<ExcelExportColumn key={index} {...colProps} />
					))} */}

					{/* <LocalizationProvider language={i18n.language}> */}
					<Grid
						style={{
							// maxHeight: "100%",
							height: currentHeight,
							paddingBottom: "30px",
							overflowY: "auto",

							width: "100%",
							maxHeight: currentHeight,
							...props.gridStyle,
						}}
						// defaultFilter={props.defaultFilter}
						// scrollable={"virtual"}
						{...dataState}
						rowHeight={34}
						sortable={sortable}
						data={newData}
						filterable={filterable}
						rows={{
							data: (row) =>
								CustomRowRender(
									row,
									handelShowRowBgColor,
									handelShowRowTextColor,
								),
						}}
						pageSize={dataState.pageSize}
						// total={gridData?.length}
						onDataStateChange={onDataStateChange}
						// onItemChange={(e) => {
						// 	console.log("Item change event:", e);
						// }}

						// onFilterChange={serverFilter ? handleFilterChange : undefined}
						// className={props.className}
						className={` ${props.className} ${
							hideVirtualScroll ? "hide-virtual-scroll" : ""
						}`}
						pageable={pageable}
						total={pageable ? result.total : undefined}
						pager={(prop) => <MyPager {...prop} pageSizes={pageSizes} />}
						group={dataState.group}
						groupable={groupable}
						cells={{
							headerCell: FilterHeaderCellRender,
							group: {
								groupHeader: props.groupHeaderCell,
							},
							...cells,
						}}
						{...props}
					>
						{props.gridColumns.map((column, key) => {
							if (
								dataState.group &&
								dataState.group.length > 0 &&
								groupLargeColumn.length > 0
							) {
								const groupColumn = groupLargeColumn.find(
									(col) => col.field === column.field,
								);

								if (groupColumn) {
									// column.width = 300;
									return (
										<GridColumn
											key={key}
											{...column}
											width={groupColumn.width}
										/>
									);
								}
							}

							return <GridColumn key={key} {...column} />;
						})}
						{/* <GridColumn title="testing " field="customerBuyer">
								{[
									<GridColumn
										title="testing one"
										field="customerBuyer"
										key={5}
									/>,
								]}
							</GridColumn> */}
						{props.lastColBtns && <GridColumn {...props.lastColBtns} />}
					</Grid>
					{/* </LocalizationProvider> */}
				</ExcelExport>
			</div>
		</div>
	);
};

export default GridWithGroupPaging;

const MyPager = (props: PagerProps) => {
	return (
		<div style={{}}>
			<Pager
				responsive={true}
				skip={props.skip}
				take={props.take}
				total={props.total}
				onPageChange={props.onPageChange}
				buttonCount={10}
				info={true}
				previousNext={true}
				messagesMap={messagesMap}
				type="numeric"
				pageSizes={props.pageSizes}
			/>
		</div>
	);
};

enum MessageKey {
	PagerFirstPage = "pager.firstPage",
	PagerPreviousPage = "pager.previousPage",
	PagerNextPage = "pager.nextPage",
	PagerLastPage = "pager.lastPage",
	PagerInfo = "pager.info",
	PagerAriaKeyshortcuts = "pager.ariaKeyshortcuts",
	PagerPage = "pager.page",
	PagerOf = "pager.of",
	PagerTotalPages = "pager.totalPages",
	PagerMobileSelect = "pager.mobileSelect",
	PagerMoreButtonLabel = "pager.moreButtonLabel",
	PagerItemsPerPage = "pager.itemsPerPage",
	PagerPageSizeAriaLabel = "pager.pageSizeAriaLabel",
}

const messagesMap = (key: string) => {
	switch (key) {
		case MessageKey.PagerInfo:
			return {
				messageKey: key,
				defaultMessage: `{0} ${t("To")} {1} ${t("Of")} {2} ${t("Lines")}`,
			};
		case MessageKey.PagerFirstPage:
			return { messageKey: key, defaultMessage: "First page" };
		case MessageKey.PagerPreviousPage:
			return { messageKey: key, defaultMessage: "Previous page" };
		case MessageKey.PagerNextPage:
			return { messageKey: key, defaultMessage: "Next page" };
		case MessageKey.PagerLastPage:
			return { messageKey: key, defaultMessage: "Last page" };
		case MessageKey.PagerItemsPerPage:
			return { messageKey: key, defaultMessage: `${t("PagerItems")}` };
		default:
			return { messageKey: key, defaultMessage: "" };
	}
};
